import React, { useState, useEffect } from "react";
import AdminSidebar from "../../components/admin/AdminSidebar";
import AdminHeader from "../../components/admin/AdminHeader";
import projectService from "../../services/projectService";
import "../../styles/admin/ManageProjects.css"; // Reusing styles for consistency
import CustomModal from "../../components/CustomModal";

function ProjectRequests() {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);

    // Modal State
    const [modalState, setModalState] = useState({
        isOpen: false,
        title: "",
        message: "",
        type: "info",
        onConfirm: null,
        confirmText: "OK",
        cancelText: "Cancel"
    });

    useEffect(() => {
        loadRequests();
    }, []);

    const loadRequests = async () => {
        try {
            setLoading(true);
            // Fetch all requests
            const allRequests = await projectService.fetchAllRequests();

            // We also need project details to show project names
            const enrichedRequests = await Promise.all(allRequests.map(async (req) => {
                try {
                    const project = await projectService.fetchProjectById(req.projectId);
                    return { ...req, projectTitle: project.title, projectStatus: project.status, projectAssignedTo: project.assignedTo };
                } catch (e) {
                    return { ...req, projectTitle: "Unknown Project", projectStatus: "Unknown", projectAssignedTo: null };
                }
            }));

            // Sort by status (pending first) and then date
            enrichedRequests.sort((a, b) => {
                if (a.status === 'pending' && b.status !== 'pending') return -1;
                if (a.status !== 'pending' && b.status === 'pending') return 1;
                return new Date(b.requestedAt) - new Date(a.requestedAt);
            });

            setRequests(enrichedRequests);
        } catch (error) {
            console.error("Error loading requests:", error);
        } finally {
            setLoading(false);
        }
    };

    const showModal = (title, message, type = 'info', onConfirm = null, confirmText = 'OK', cancelText = 'Cancel') => {
        setModalState({
            isOpen: true,
            title,
            message,
            type,
            onConfirm: onConfirm ? () => { onConfirm(); setModalState(prev => ({ ...prev, isOpen: false })); } : null,
            confirmText,
            cancelText
        });
    };

    const closeModal = () => {
        setModalState(prev => ({ ...prev, isOpen: false }));
    };

    const handleApprove = (request) => {
        showModal(
            "Approve Request",
            `Approve request from ${request.userName} for "${request.projectTitle}"?`,
            "confirm",
            () => confirmApprove(request),
            "Approve",
            "Cancel"
        );
    };

    const confirmApprove = async (request) => {
        try {
            // 1. Assign Project to User
            if (request.projectAssignedTo === 'ALL') {
                // Formatting for ALL projects: Clone it so original stays in marketplace
                await projectService.assignFreelancerClone(request.projectId, request.userId, request.userName);
            } else {
                // Standard project: Assign directly (removes from marketplace)
                await projectService.assignFreelancer(request.projectId, request.userId, request.userName);
            }

            // 2. Update Request Status to 'approved'
            await projectService.updateRequestStatus(request._id, 'approved');

            showModal("Success", "Request approved and project assigned!", "success");
            loadRequests();
        } catch (error) {
            console.error("Error approving request:", error);
            showModal("Error", "Failed to approve request.", "error");
        }
    };

    const handleReject = (request) => {
        showModal(
            "Reject Request",
            `Reject request from ${request.userName}?`,
            "danger",
            () => confirmReject(request),
            "Reject",
            "Cancel"
        );
    };

    const confirmReject = async (request) => {
        try {
            // 1. Update Request Status to 'rejected'
            await projectService.updateRequestStatus(request._id, 'rejected');

            showModal("Success", "Request rejected.", "success");
            loadRequests();
        } catch (error) {
            console.error("Error rejecting request:", error);
            showModal("Error", "Failed to reject request.", "error");
        }
    };

    return (
        <div className="admin-layout">
            <AdminSidebar />
            <div className="admin-content">
                <AdminHeader title="Project Requests" />

                {loading ? (
                    <p style={{ padding: '0 2rem' }}>Loading requests...</p>
                ) : (
                    <div className="projects-table-wrapper" style={{ padding: '0 2rem' }}>
                        <table className="projects-table">
                            <thead>
                                <tr>
                                    <th>User</th>
                                    <th>Project</th>
                                    <th>Date</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {requests.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" style={{ textAlign: 'center' }}>No requests found</td>
                                    </tr>
                                ) : (
                                    requests.map((req) => (
                                        <tr key={req._id} style={{ opacity: req.status !== 'pending' ? 0.6 : 1 }}>
                                            <td>{req.userName}</td>
                                            <td>{req.projectTitle}</td>
                                            <td>{new Date(req.requestedAt).toLocaleDateString()}</td>
                                            <td>
                                                <span style={{
                                                    padding: '0.25rem 0.75rem',
                                                    borderRadius: '100px',
                                                    fontSize: '0.85rem',
                                                    fontWeight: '600',
                                                    background:
                                                        req.status === 'approved' ? '#f6ffed' :
                                                            req.status === 'rejected' ? '#fff1f0' : '#e6f7ff',
                                                    color:
                                                        req.status === 'approved' ? '#52c41a' :
                                                            req.status === 'rejected' ? '#ff4d4f' : '#1890ff',
                                                    border: `1px solid ${req.status === 'approved' ? '#b7eb8f' :
                                                        req.status === 'rejected' ? '#ffa39e' : '#91d5ff'
                                                        }`
                                                }}>
                                                    {req.status.toUpperCase()}
                                                </span>
                                            </td>
                                            <td>
                                                {req.status === 'pending' && (
                                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                        <button
                                                            onClick={() => handleApprove(req)}
                                                            style={{
                                                                background: '#52c41a',
                                                                color: 'white',
                                                                border: 'none',
                                                                padding: '0.4rem 0.8rem',
                                                                borderRadius: '4px',
                                                                cursor: 'pointer',
                                                                fontSize: '0.9rem'
                                                            }}
                                                        >
                                                            Approve
                                                        </button>
                                                        <button
                                                            onClick={() => handleReject(req)}
                                                            style={{
                                                                background: '#ff4d4f',
                                                                color: 'white',
                                                                border: 'none',
                                                                padding: '0.4rem 0.8rem',
                                                                borderRadius: '4px',
                                                                cursor: 'pointer',
                                                                fontSize: '0.9rem'
                                                            }}
                                                        >
                                                            Reject
                                                        </button>
                                                    </div>
                                                )}
                                                {req.status !== 'pending' && (
                                                    <span style={{ color: '#888', fontSize: '0.9rem' }}>
                                                        {req.status === 'approved' ? 'Assigned' : 'Closed'}
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            <CustomModal
                isOpen={modalState.isOpen}
                onClose={closeModal}
                title={modalState.title}
                message={modalState.message}
                type={modalState.type}
                onConfirm={modalState.onConfirm}
                confirmText={modalState.confirmText}
                cancelText={modalState.cancelText}
            />
        </div>
    );
}

export default ProjectRequests;
